/**
 * Strict .env text <-> entries, used by the bulk editor on the project
 * Environments page. The pair must round-trip exactly: opening the editor and
 * saving without touching anything has to be a no-op for every stored value
 * (newlines, quotes, `#`, leading/trailing spaces included).
 *
 * Deliberately dependency-free — the web bundle imports this file directly.
 */

export interface DotenvEntry {
  key: string;
  value: string;
}

export interface DotenvError {
  /** 1-based line number in the source text. */
  line: number;
  message: string;
}

const KEY = /^[A-Z_][A-Z0-9_]*$/i;

/** Values that survive being written bare after `KEY=`. */
function isBareSafe(value: string): boolean {
  if (value === "") return true;
  if (/[\r\n]/.test(value)) return false;
  if (value !== value.trim()) return false;
  if (/^['"]/.test(value)) return false;
  // ` #` starts an inline comment on an unquoted value.
  if (/\s#/.test(value)) return false;
  return true;
}

export function serializeDotenv(entries: DotenvEntry[]): string {
  return entries
    .map(({ key, value }) => {
      if (isBareSafe(value)) return `${key}=${value}`;
      const escaped = value
        .replace(/\\/g, "\\\\")
        .replace(/"/g, '\\"')
        .replace(/\n/g, "\\n")
        .replace(/\r/g, "\\r");
      return `${key}="${escaped}"`;
    })
    .join("\n");
}

export function parseDotenv(text: string): { entries: DotenvEntry[]; errors: DotenvError[] } {
  const entries: DotenvEntry[] = [];
  const errors: DotenvError[] = [];
  const seen = new Set<string>();
  const lines = text.split(/\r?\n/);

  for (let i = 0; i < lines.length; i++) {
    const lineNo = i + 1;
    const line = lines[i]!.replace(/^\s*export\s+/, "").trimStart();
    if (!line.trim() || line.startsWith("#")) continue;

    const eq = line.indexOf("=");
    if (eq <= 0) {
      errors.push({ line: lineNo, message: "expected KEY=value" });
      continue;
    }
    const key = line.slice(0, eq).trim();
    if (!KEY.test(key)) {
      errors.push({ line: lineNo, message: `invalid variable name "${key}"` });
      continue;
    }

    let rest = line.slice(eq + 1).trimStart();
    let value: string;
    const quote = rest[0];
    if (quote === '"' || quote === "'") {
      // Quoted values may span lines (pasted private keys, JSON blobs).
      let body = rest.slice(1);
      let end = closingQuote(body, quote);
      while (end === -1 && i + 1 < lines.length) {
        i += 1;
        body += "\n" + lines[i]!;
        end = closingQuote(body, quote);
      }
      if (end === -1) {
        errors.push({ line: lineNo, message: `unterminated ${quote} quote for ${key}` });
        continue;
      }
      const trailing = body.slice(end + 1).trim();
      if (trailing && !trailing.startsWith("#")) {
        errors.push({ line: lineNo, message: `unexpected text after closing quote for ${key}` });
        continue;
      }
      const raw = body.slice(0, end);
      value = quote === '"' ? unescapeDouble(raw) : raw;
    } else {
      const comment = rest.search(/\s#/);
      if (comment !== -1) rest = rest.slice(0, comment);
      value = rest.trim();
    }

    if (seen.has(key)) {
      errors.push({ line: lineNo, message: `duplicate variable ${key}` });
      continue;
    }
    seen.add(key);
    entries.push({ key, value });
  }
  return { entries, errors };
}

/** Index of the first unescaped closing quote, or -1. */
function closingQuote(body: string, quote: string): number {
  for (let j = 0; j < body.length; j++) {
    const ch = body[j];
    // Backslash escapes only exist inside double quotes.
    if (quote === '"' && ch === "\\") {
      j += 1;
      continue;
    }
    if (ch === quote) return j;
  }
  return -1;
}

function unescapeDouble(raw: string): string {
  // Unknown escapes (`\p` in a Windows path, `\d` in a regex) stay verbatim.
  return raw.replace(/\\([\\"nr])/g, (_m, c: string) => (c === "n" ? "\n" : c === "r" ? "\r" : c));
}
